import { AxiosResponse } from 'axios';
import api from 'config/api.config';

// ==================== Interfaces ====================

// Price structure
export interface SubscriptionPrice {
  amount_monthly?: number;
  amount_annually?: number;
  amount_4weeks: number;
  amount_4weeks_annual: number;
  discount_percentage?: number;
  currency: string;
}

// Stripe price IDs
export interface StripePriceIds {
  monthly: string;
  annual: string;
}

// Subscription stats
export interface SubscriptionStats {
  activeUsers: number;
  monthlyUsers: number;
  annualUsers: number;
  pendingPayments?: number;
  totalRevenue?: number;
}

// Subscription action
export interface SubscriptionAction {
  action_id: string;
  action_limit: number;
  limit_unit?: 'day' | 'week';
}

// Subscription summary item
export interface SubscriptionSummaryItem {
  _id: string;
  subscription_name: string;
  tier: number;
  price: {
    amount_4weeks: number;
    amount_4weeks_annual: number;
  };
  activeUsers: number;
  monthlyUsers: number;
  annualUsers: number;
}

// Summary totals
export interface SummaryTotals {
  activeUsers: number;
  monthlyUsers: number;
  annualUsers: number;
  pendingPayments: number;
}

// Revenue item
export interface RevenueItem {
  _id: 'monthly' | 'annual';
  totalRevenue: number;
  count: number;
}

// Summary response
export interface SubscriptionsSummaryResponse {
  success: boolean;
  data: {
    subscriptions: SubscriptionSummaryItem[];
    totals: SummaryTotals;
    revenue: RevenueItem[];
  };
}

// Multilingual text field
export type LocalizedText = string | { en: string; fr: string };

// Full subscription detail
export interface SubscriptionDetail {
  _id: string;
  subscription_name: string;
  description: LocalizedText;
  tagline?: LocalizedText;
  target_audience?: string;
  tenure: number;
  price: SubscriptionPrice;
  stripe_price_ids: StripePriceIds;
  number_of_accounts: number;
  actions?: SubscriptionAction[];
  support_level?: string;
  setup_type?: string;
  tier: number;
  status: string;
  is_active: boolean;
  stats?: SubscriptionStats;
}

// Subscriptions list response
export interface SubscriptionsListResponse {
  success: boolean;
  data: SubscriptionDetail[];
}

// Single subscription response
export interface SubscriptionResponse {
  success: boolean;
  data: SubscriptionDetail;
}

// Update subscription payload
export interface UpdateSubscriptionPayload {
  price?: Partial<SubscriptionPrice>;
  stripe_price_ids?: Partial<StripePriceIds>;
  description?: LocalizedText;
  tagline?: LocalizedText;
  target_audience?: string;
  number_of_accounts?: number;
  actions?: SubscriptionAction[];
  support_level?: string;
  setup_type?: string;
  status?: string;
  is_active?: boolean;
}

// Update subscription response
export interface UpdateSubscriptionResponse {
  success: boolean;
  message: string;
  data: SubscriptionDetail;
}

// User info in subscription users
export interface SubscriptionUserInfo {
  email: string;
  firstName: string;
  lastName: string;
  createdAt?: string;
}

// Subscription user
export interface SubscriptionUser {
  _id: string;
  user_id: string;
  billing_cycle: 'monthly' | 'annual';
  subscription_start_date: string;
  subscription_end_date: string;
  last_payment_amount: number;
  last_payment_date?: string;
  status: string;
  payment_method: string;
  invoice_number?: string;
  user: SubscriptionUserInfo;
  subscription?: {
    subscription_name: string;
    tier: number;
  };
  daysExpired?: number;
  amount?: number;
  createdAt?: string;
  payment_reminder_count?: number;
  payment_reminder_last_sent?: string;
}

// Subscription users response
export interface SubscriptionUsersResponse {
  success: boolean;
  data: {
    subscription?: {
      _id: string;
      name: string;
      tier: number;
    };
    users: SubscriptionUser[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

// Annual users response
export interface AnnualUsersResponse {
  success: boolean;
  data: {
    users: SubscriptionUser[];
    stats: {
      totalAnnualUsers: number;
      bySubscription: {
        subscriptionName: string;
        count: number;
        totalRevenue: number;
      }[];
    };
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

// Payment pending users response
export interface PaymentPendingUsersResponse {
  success: boolean;
  data: {
    users: SubscriptionUser[];
    stats: {
      totalPendingPayments: number;
      bySubscription: {
        subscriptionName: string;
        count: number;
        totalPending: number;
      }[];
    };
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

// Expired users response
export interface ExpiredUsersResponse {
  success: boolean;
  data: {
    users: SubscriptionUser[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

// Bulk update payload
export interface BulkUpdatePayload {
  userIds: string[];
  action: 'extend' | 'suspend' | 'cancel';
  days?: number;
}

// Bulk update response
export interface BulkUpdateResponse {
  success: boolean;
  message: string;
  data: {
    action: string;
    usersAffected: number;
    days?: number;
  };
}

// Query params for users endpoints
export interface SubscriptionUsersQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  billingCycle?: 'monthly' | 'annual';
}

export interface AnnualUsersQueryParams {
  page?: number;
  limit?: number;
  subscriptionName?: string;
}

export interface PaymentPendingQueryParams {
  page?: number;
  limit?: number;
  subscriptionName?: string;
}

export interface ExpiredUsersQueryParams {
  page?: number;
  limit?: number;
  daysAgo?: number;
}

// ==================== API Functions ====================

/**
 * Get subscriptions summary
 */
export const getSubscriptionsSummary = async (): Promise<SubscriptionsSummaryResponse> => {
  try {
    const response: AxiosResponse<SubscriptionsSummaryResponse> = await api.get(
      `/api/admin/subscriptions/summary`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscriptions summary');
  }
};

/**
 * Get all subscriptions
 */
export const getAllSubscriptions = async (): Promise<SubscriptionsListResponse> => {
  try {
    const response: AxiosResponse<SubscriptionsListResponse> = await api.get(
      `/api/admin/subscriptions`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscriptions');
  }
};

/**
 * Get single subscription by ID
 */
export const getSubscriptionById = async (subscriptionId: string): Promise<SubscriptionResponse> => {
  try {
    const response: AxiosResponse<SubscriptionResponse> = await api.get(
      `/api/admin/subscriptions/${subscriptionId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscription');
  }
};

/**
 * Update subscription
 */
export const updateSubscription = async (
  subscriptionId: string,
  payload: UpdateSubscriptionPayload
): Promise<UpdateSubscriptionResponse> => {
  try {
    const response: AxiosResponse<UpdateSubscriptionResponse> = await api.put(
      `/api/admin/subscriptions/${subscriptionId}`,
      payload
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update subscription');
  }
};

/**
 * Get users by subscription name
 */
export const getUsersBySubscription = async (
  subscriptionName: string,
  params?: SubscriptionUsersQueryParams
): Promise<SubscriptionUsersResponse> => {
  try {
    const queryParams: any = {
      page: params?.page || 1,
      limit: params?.limit || 10,
    };

    if (params?.status) queryParams.status = params.status;
    if (params?.billingCycle) queryParams.billingCycle = params.billingCycle;

    const response: AxiosResponse<SubscriptionUsersResponse> = await api.get(
      `/api/admin/subscriptions/${encodeURIComponent(subscriptionName)}/users`,
      { params: queryParams }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscription users');
  }
};

/**
 * Get annual subscription users
 */
export const getAnnualUsers = async (params?: AnnualUsersQueryParams): Promise<AnnualUsersResponse> => {
  try {
    const queryParams: any = {
      page: params?.page || 1,
      limit: params?.limit || 20,
    };

    if (params?.subscriptionName) queryParams.subscriptionName = params.subscriptionName;

    const response: AxiosResponse<AnnualUsersResponse> = await api.get(
      `/api/admin/users/annual`,
      { params: queryParams }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch annual users');
  }
};

/**
 * Get payment pending users
 */
export const getPaymentPendingUsers = async (params?: PaymentPendingQueryParams): Promise<PaymentPendingUsersResponse> => {
  try {
    const queryParams: any = {
      page: params?.page || 1,
      limit: params?.limit || 20,
    };

    if (params?.subscriptionName) queryParams.subscriptionName = params.subscriptionName;

    const response: AxiosResponse<PaymentPendingUsersResponse> = await api.get(
      `/api/admin/users/payment-pending`,
      { params: queryParams }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch payment pending users');
  }
};

/**
 * Get expired subscription users
 */
export const getExpiredUsers = async (params?: ExpiredUsersQueryParams): Promise<ExpiredUsersResponse> => {
  try {
    const queryParams: any = {
      page: params?.page || 1,
      limit: params?.limit || 20,
    };

    if (params?.daysAgo) queryParams.daysAgo = params.daysAgo;

    const response: AxiosResponse<ExpiredUsersResponse> = await api.get(
      `/api/admin/users/expired`,
      { params: queryParams }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch expired users');
  }
};

/**
 * Bulk update user subscriptions
 */
export const bulkUpdateSubscriptions = async (payload: BulkUpdatePayload): Promise<BulkUpdateResponse> => {
  try {
    const response: AxiosResponse<BulkUpdateResponse> = await api.post(
      `/api/admin/subscriptions/bulk-update`,
      payload
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to bulk update subscriptions');
  }
};

// ==================== Action Management Interfaces ====================

// Action definition (for available actions not yet assigned)
export interface Action {
  _id: string;
  name: string;
  friendly_name?: string;
  description?: string;
  category?: string;
  platform?: string;
  type?: string;
  is_active?: boolean;
}

// Action with limit for subscription (assigned actions)
export interface ActionWithLimit {
  action_id: string;
  name: string;
  friendly_name?: string;
  category?: string;
  platform?: string;
  action_limit: number;
  limit_unit?: 'day' | 'week';
}

// Get all actions response
export interface AllActionsResponse {
  success: boolean;
  data: Action[];
}

// Subscription actions response (matches actual API)
export interface SubscriptionActionsResponse {
  success: boolean;
  data: {
    subscription: {
      _id: string;
      name: string;
      tier: number;
    };
    actions: ActionWithLimit[];
    availableActions: Action[];
  };
}

// Update action limit payload
export interface UpdateActionLimitPayload {
  action_limit: number;
  limit_unit?: 'day' | 'week';
}

// Bulk update actions payload
export interface BulkUpdateActionsPayload {
  updates: {
    action_id: string;
    action_limit: number;
  }[];
}

// Add action payload
export interface AddActionPayload {
  action_id: string;
  action_limit: number;
  limit_unit?: 'day' | 'week';
}

// Action update response
export interface ActionUpdateResponse {
  success: boolean;
  message: string;
  data?: any;
}

// ==================== Action Management API Functions ====================

/**
 * Get all available actions
 */
export const getAllActions = async (): Promise<AllActionsResponse> => {
  try {
    const response: AxiosResponse<AllActionsResponse> = await api.get(
      `/api/admin/actions`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch actions');
  }
};

/**
 * Get subscription actions with limits
 */
export const getSubscriptionActions = async (subscriptionId: string): Promise<SubscriptionActionsResponse> => {
  try {
    const response: AxiosResponse<SubscriptionActionsResponse> = await api.get(
      `/api/admin/subscriptions/${subscriptionId}/actions`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subscription actions');
  }
};

/**
 * Replace all subscription actions
 */
export const replaceSubscriptionActions = async (
  subscriptionId: string,
  actions: ActionWithLimit[]
): Promise<ActionUpdateResponse> => {
  try {
    const response: AxiosResponse<ActionUpdateResponse> = await api.put(
      `/api/admin/subscriptions/${subscriptionId}/actions`,
      { actions }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to replace subscription actions');
  }
};

/**
 * Add a single action to subscription
 */
export const addActionToSubscription = async (
  subscriptionId: string,
  payload: AddActionPayload
): Promise<ActionUpdateResponse> => {
  try {
    const response: AxiosResponse<ActionUpdateResponse> = await api.post(
      `/api/admin/subscriptions/${subscriptionId}/actions`,
      payload
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to add action to subscription');
  }
};

/**
 * Bulk update action limits
 */
export const bulkUpdateActionLimits = async (
  subscriptionId: string,
  payload: BulkUpdateActionsPayload
): Promise<ActionUpdateResponse> => {
  try {
    const response: AxiosResponse<ActionUpdateResponse> = await api.patch(
      `/api/admin/subscriptions/${subscriptionId}/actions/bulk`,
      payload
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to bulk update action limits');
  }
};

/**
 * Update single action limit
 */
export const updateActionLimit = async (
  subscriptionId: string,
  actionId: string,
  payload: UpdateActionLimitPayload
): Promise<ActionUpdateResponse> => {
  try {
    const response: AxiosResponse<ActionUpdateResponse> = await api.patch(
      `/api/admin/subscriptions/${subscriptionId}/actions/${actionId}`,
      payload
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update action limit');
  }
};

/**
 * Remove action from subscription
 */
export const removeActionFromSubscription = async (
  subscriptionId: string,
  actionId: string
): Promise<ActionUpdateResponse> => {
  try {
    const response: AxiosResponse<ActionUpdateResponse> = await api.delete(
      `/api/admin/subscriptions/${subscriptionId}/actions/${actionId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to remove action from subscription');
  }
};
